import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toastUtil } from '../../utils/toast'
import { adminApi } from '../api'
import {
  ADMIN_INQUIRY_STATUS_LABEL,
  type AdminInquiryDetail,
  type InquiryStatus,
} from '../api/inquiry'

interface Props {
  detail: AdminInquiryDetail
  /** 답변·상태 변경 뒤 목록과 상세를 다시 불러오기 위해 */
  onChanged: () => void
  onClose: () => void
}

const statusChip = (status: InquiryStatus): string => {
  if (status === 'open') return 'bg-[#e08a3c]/15 text-[#c4742c]'
  if (status === 'answered') return 'bg-[#4a8a5c]/15 text-[#3b7049]'
  return 'bg-brandPrimary-10% text-textColorText3'
}

const AdminInquiryDetailModal: React.FC<Props> = ({
  detail,
  onChanged,
  onClose,
}) => {
  const navigate = useNavigate()
  const [body, setBody] = useState('')
  const [isBusy, setIsBusy] = useState(false)

  const { inquiry, user, messages } = detail

  const handleReply = async () => {
    const text = body.trim()
    if (text.length === 0) {
      toastUtil.error('답변 내용을 입력해 주세요')
      return
    }
    if (isBusy) return
    setIsBusy(true)
    try {
      await adminApi.replyInquiry(inquiry.id, text)
      toastUtil.success('답변을 보냈습니다')
      setBody('')
      onChanged()
    } catch {
      toastUtil.error('답변을 보내지 못했습니다')
    } finally {
      setIsBusy(false)
    }
  }

  const handleStatus = async (status: InquiryStatus) => {
    if (isBusy) return
    setIsBusy(true)
    try {
      await adminApi.updateInquiryStatus(inquiry.id, status)
      toastUtil.success('상태를 바꿨습니다')
      onChanged()
    } catch {
      toastUtil.error('상태를 바꾸지 못했습니다')
    } finally {
      setIsBusy(false)
    }
  }

  const tag = (text: string) => (
    <span
      key={text}
      className="px-9px py-4px rounded-12px bg-4B3621-80%-5% text-style-captionM text-textColorText2"
    >
      {text}
    </span>
  )

  return (
    <div className="p-20px flex flex-col gap-16px">
      {/* 태그 줄 */}
      <div className="flex flex-wrap items-center gap-6px">
        <span
          className={`px-10px py-4px rounded-12px text-style-captionM ${statusChip(inquiry.status)}`}
        >
          {ADMIN_INQUIRY_STATUS_LABEL[inquiry.status]}
        </span>
        {tag(inquiry.category_label)}
        {inquiry.app_version && tag(`v${inquiry.app_version}`)}
        {inquiry.app_platform && tag(inquiry.app_platform)}
        {tag(`접수 ${inquiry.created_at}`)}
      </div>

      {/* 문의한 사람 */}
      <div className="p-16px rounded-10px bg-4B3621-80%-5% flex flex-col gap-6px">
        <div className="text-style-captionM text-textColorText3">문의한 사람</div>
        {user ? (
          <>
            <div className="flex flex-wrap items-center gap-10px">
              <span className="text-style-labelL text-textColorText2">
                {user.nickname} ({user.username})
              </span>
              <span className="text-style-body2 text-textColorText2">
                {user.phone || '번호 없음'}
              </span>
              <span className="text-style-body2 text-textColorText2">
                가입 {user.created_at}
              </span>
              <span className="text-style-body2 text-textColorText2">
                기록 {user.review_count} · 저장 {user.saved_place_count}
              </span>
              {!user.is_active && (
                <span className="px-8px py-2px rounded-8px bg-semanticError/10 text-semanticError text-style-captionM">
                  탈퇴
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(`/admin/users?search=${user.username}`)}
              className="self-start mt-4px px-10px py-4px rounded-8px border border-brandPrimary-20% text-style-captionM text-textColorText2 hover:bg-white"
            >
              사용자 관리에서 보기
            </button>
          </>
        ) : (
          <span className="text-style-body2 text-textColorText3">
            탈퇴한 사용자입니다.
          </span>
        )}
      </div>

      {/* 대화 */}
      <div className="flex flex-col gap-10px max-h-[40vh] overflow-y-auto scrollbar-hide">
        {messages.map((m) => {
          const isAdmin = m.sender === 'admin'
          return (
            <div
              key={m.id}
              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-14px py-12px rounded-12px flex flex-col gap-6px ${
                  isAdmin
                    ? 'bg-4B3621-80%-10%'
                    : 'bg-backgroundColorBg2 border border-brandPrimary-10%'
                }`}
              >
                <div className="flex items-center gap-10px">
                  <span className="text-style-captionM text-textColorText2 font-bold">
                    {m.author}
                  </span>
                  <span className="flex-1 text-right text-style-captionM text-textColorText3">
                    {m.created_at}
                  </span>
                </div>
                {/* 줄바꿈을 살린다. React 라 이스케이프는 기본이다. */}
                <p className="text-style-body2 text-textColorText2 whitespace-pre-wrap break-words">
                  {m.body}
                </p>
                {/* 첨부 사진 — 누르면 원본을 새 탭으로 */}
                {m.photos && m.photos.length > 0 && (
                  <div className="mt-8px flex flex-wrap gap-8px">
                    {m.photos.map((src) => (
                      <a key={src} href={src} target="_blank" rel="noreferrer">
                        <img src={src} alt="첨부 사진" className="w-120px h-150px rounded-8px object-cover bg-backgroundColorBg2" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* 답변 */}
      <div className="flex flex-col gap-12px">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="답변을 적어 주세요."
          maxLength={5000}
          rows={4}
          className="
            w-full py-12px px-14px
            border border-brandPrimary-20% rounded-10px bg-transparent
            text-style-body1 text-textColorText2
            focus:outline-none focus:border-brandPrimary
            placeholder:text-textColorText3 resize-none
          "
        />
        <div className="flex flex-wrap items-center gap-8px">
          <button
            type="button"
            onClick={handleReply}
            disabled={isBusy}
            className="py-10px px-16px rounded-8px bg-4B3621-80% text-white text-style-button2 disabled:opacity-50"
          >
            답변 보내기
          </button>
          <button
            type="button"
            onClick={() => handleStatus('closed')}
            disabled={isBusy || inquiry.status === 'closed'}
            className="py-10px px-16px rounded-8px border border-brandPrimary-20% text-style-button2 text-textColorText2 disabled:opacity-40"
          >
            종료 처리
          </button>
          <button
            type="button"
            onClick={() => handleStatus('open')}
            disabled={isBusy || inquiry.status === 'open'}
            className="py-10px px-16px rounded-8px border border-brandPrimary-20% text-style-button2 text-textColorText2 disabled:opacity-40"
          >
            대기로 되돌리기
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto py-10px px-16px rounded-8px text-style-button2 text-textColorText3"
          >
            닫기
          </button>
        </div>
        <p className="text-style-captionM text-textColorText3">
          답변을 보내면 상태가 ‘답변함’으로 바뀌고, 문의한 사람에게 앱 알림과 푸시가
          나갑니다. 푸시는 저장이 끝난 뒤에 보내므로 푸시가 실패해도 답변은 남습니다.
        </p>
      </div>
    </div>
  )
}

export default AdminInquiryDetailModal
